import * as React from "react";
import {Grid, Tab, Tabs} from "@material-ui/core";
import {ContentHeader} from "../ui-base/content-header";
import {useCallback, useEffect, useState} from "react";
import {toggleLoader} from "../../store/global/global-slice";
import {PublisherService} from "../../services/publisher.service";
import {useDispatch, useSelector} from "react-redux";
import {PublisherSimilarBookListModel} from "../../models/publisher/publisher-similar-book-list.model";
import {useHistory} from "react-router-dom";
import {ProfileDashboardComponent} from "../dashboard/profile-dashboard-component";
import {InfoDashobardComponent} from "../dashboard/info-dashboard-component";
import {NewPublisherBookShort} from "./books/new-publisher-book-short";
import {CustomTabPanel} from "../ui-base/tab-panel";
import {ApplicationStatusEnum} from "../../models/application-status.enum";
import {EmptyBookShort} from "../author/books/empty-book-short";
import {StyledButton} from "../ui-base/button";
import {AuthMeModel} from "../../models/auth-me.model";
import {RootState} from "../../store/root-reducer";
import {quotesPublisher} from "../../data/quotes-publisher";
import {LastSearchData} from "../../models/view/last-search-data.model";
import {ListData} from "../../models/view/list-data.model";
import {ForeignBooksDashboard} from "./foreign-books-dashboard";
import {useTranslation} from "react-i18next";
import {AccountTypeEnum} from "../../models/account-type.enum";

const PublisherDashboard = () => {
    const [manuscriptsData, setManuscriptsData] = useState<ManuscriptsData>({newestManuscripts: {}, bookmarkManuscripts: {}, notPublishManuscripts: {}, toPublishManuscripts: {}} as ManuscriptsData);
    const [lastSearchData, setLastSearchData] = useState<LastSearchData>({searchValue: '', sortFieldValue: 'submitted_date', sortValue: 'desc'});
    const [quote, setQuote] = React.useState('');
    const [manuscriptsLoaded, setManuscriptsLoaded] = useState<boolean>(false);
    const [tabActive, setTabActive] = React.useState(0);
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation('publisher');

    const user: AuthMeModel = useSelector(
        (state: RootState) => state.auth.user
    );

    const loadNewestManuscripts = useCallback(async (sortFieldTmp, sortValueTmp, searchValueTmp, page = 0, perPage = 5) => {
        const newestManuscriptsLoaded = await PublisherService.getApplications(searchValueTmp, [ApplicationStatusEnum.Submitted, ApplicationStatusEnum.OpenByPub], false, sortFieldTmp, sortValueTmp, page, perPage);

        setManuscriptsData(prevValue => ({
            ...prevValue,
            newestManuscripts: {
                items: (page > 0) ? [...prevValue.newestManuscripts.items, ...newestManuscriptsLoaded.results] : newestManuscriptsLoaded.results,
                nextPage: !!newestManuscriptsLoaded.next,
                total: newestManuscriptsLoaded.count
            }
        }));
    }, []);

    const loadToPublishManuscripts = useCallback(async (sortFieldTmp, sortValueTmp, searchValueTmp, page = 0, perPage = 5) => {
        const toPublishManuscriptsLoaded = await PublisherService.getApplications(searchValueTmp, [ApplicationStatusEnum.AutWant, ApplicationStatusEnum.PubWant], false, sortFieldTmp, sortValueTmp, page, perPage);

        setManuscriptsData(prevValue => ({
            ...prevValue,
            toPublishManuscripts: {
                items: (page > 0) ? [...prevValue.toPublishManuscripts.items, ...toPublishManuscriptsLoaded.results] : toPublishManuscriptsLoaded.results,
                nextPage: !!toPublishManuscriptsLoaded.next,
                total: toPublishManuscriptsLoaded.count
            }
        }));
    }, []);

    const loadBookmarkManuscripts = useCallback(async (sortFieldTmp, sortValueTmp, searchValueTmp, page = 0, perPage = 5) => {
        const bookmarkManuscriptsLoaded = await PublisherService.getApplications(searchValueTmp, [], true, sortFieldTmp, sortValueTmp, page, perPage);

        setManuscriptsData(prevValue => ({
            ...prevValue,
            bookmarkManuscripts: {
                items: (page > 0) ? [...prevValue.bookmarkManuscripts.items, ...bookmarkManuscriptsLoaded.results] : bookmarkManuscriptsLoaded.results,
                nextPage: !!bookmarkManuscriptsLoaded.next,
                total: bookmarkManuscriptsLoaded.count
            }
        }));
    }, []);

    const loadNotPublishManuscripts = useCallback(async (sortFieldTmp, sortValueTmp, searchValueTmp, page = 0, perPage = 5) => {
        const notPublishManuscriptsLoaded = await PublisherService.getApplications(searchValueTmp, [ApplicationStatusEnum.AutNotWant, ApplicationStatusEnum.PubNotWant, ApplicationStatusEnum.Failed], false, sortFieldTmp, sortValueTmp, page, perPage);

        setManuscriptsData(prevValue => ({
            ...prevValue,
            notPublishManuscripts: {
                items: (page > 0) ? [...prevValue.notPublishManuscripts.items, ...notPublishManuscriptsLoaded.results] : notPublishManuscriptsLoaded.results,
                nextPage: !!notPublishManuscriptsLoaded.next,
                total: notPublishManuscriptsLoaded.count
            }
        }));
    }, []);

    const loadManuscripts = useCallback(async (sortFieldTmp, sortValueTmp, searchValueTmp) => {
        dispatch(toggleLoader(true));

        await loadNewestManuscripts(sortFieldTmp, sortValueTmp, searchValueTmp);
        await loadToPublishManuscripts(sortFieldTmp, sortValueTmp, searchValueTmp);
        await loadBookmarkManuscripts(sortFieldTmp, sortValueTmp, searchValueTmp);
        await loadNotPublishManuscripts(sortFieldTmp, sortValueTmp, searchValueTmp)

        setManuscriptsLoaded(true);
        dispatch(toggleLoader(false));
    }, [dispatch, loadNewestManuscripts, loadToPublishManuscripts, loadBookmarkManuscripts, loadNotPublishManuscripts]);

    useEffect(() => {
        dispatch(toggleLoader(true));

        if (user.publisher && user.status !== 'AWAITING_FOR_VERIFICATION') {
            loadManuscripts(lastSearchData.sortFieldValue, lastSearchData.sortValue, lastSearchData.searchValue);
        } else {
            dispatch(toggleLoader(false));
            setManuscriptsLoaded(true);
        }

    }, [dispatch, user, lastSearchData, loadManuscripts]);

    useEffect(() => {
        const quotesLength = quotesPublisher.length;
        const min = Math.ceil(0);
        const max = Math.floor(quotesLength - 1);
        const randomNumber = Math.floor(Math.random() * (max - min)) + min;

        setQuote(t(`dashboard.quoteDesc${randomNumber}`));
    }, [t]);

    return (
        <Grid container direction={"column"}>
            <Grid item container direction={"row"} spacing={2}>
                <Grid item container direction={"row"} spacing={2}>
                    <Grid item sm={12} md={6}>
                        <ProfileDashboardComponent
                            title={t('dashboard.welcomeTitle')}
                            description={t('dashboard.welcomeDesc')}
                            linkTitle={t('dashboard.fillProfile')}
                            link={'/publisher/edit-profile'}
                        />
                    </Grid>

                    <Grid item sm={12} md={6}>
                        <InfoDashobardComponent
                            title={t('dashboard.quoteTitle')}
                            description={quote}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <ForeignBooksDashboard/>

            <Grid item container direction={"column"} spacing={2}>
                <Grid item>
                    <ContentHeader title={t('publishProposals.title')}/>
                </Grid>

                <Grid item>
                    <Tabs value={tabActive} onChange={(event, newValue) => { setTabActive(newValue) }}>
                        <Tab label={t('publishProposals.newestProposals') + ` (${manuscriptsData?.newestManuscripts?.total || 0})`}/>
                        <Tab label={t('publishProposals.saved') + ` (${manuscriptsData?.bookmarkManuscripts?.total || 0})`}/>
                        <Tab label={t('publishProposals.toPublish') + ` (${manuscriptsData?.toPublishManuscripts?.total || 0})`}/>
                        <Tab label={t('publishProposals.rejected') + ` (${manuscriptsData?.notPublishManuscripts?.total || 0})`}/>
                    </Tabs>

                    <CustomTabPanel value={tabActive} index={0}>
                        <Grid item container direction={"row"} spacing={2}>
                            {manuscriptsData.newestManuscripts?.items?.map((application: PublisherSimilarBookListModel, index) => (
                                <NewPublisherBookShort key={index} manuscript={application}/>
                            ))}
                        </Grid>

                        {manuscriptsData.newestManuscripts?.items?.length === 0 && manuscriptsLoaded &&
                            <EmptyBookShort
                                title={t('publishProposals.notFound')}
                                description={''}
                                showButton={false}
                            />
                        }

                        {manuscriptsData.newestManuscripts?.total > 5 &&
                        <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                            <StyledButton type={"button"} color={"transparent"} onClick={() => {
                                history.push('/publisher/manuscripts');
                            }}>
                                {t('publishProposals.showAll')}
                            </StyledButton>
                        </Grid>
                        }
                    </CustomTabPanel>

                    <CustomTabPanel value={tabActive} index={1}>
                        <Grid item container direction={"row"}>
                            {manuscriptsData.bookmarkManuscripts?.items?.map((application: PublisherSimilarBookListModel, index) => (
                                <NewPublisherBookShort key={index} manuscript={application}/>
                            ))}
                        </Grid>

                        {manuscriptsData.bookmarkManuscripts?.items?.length === 0 && manuscriptsLoaded &&
                            <EmptyBookShort
                                title={t('publishProposals.notFound')}
                                description={''}
                                showButton={false}
                            />
                        }

                        {manuscriptsData.bookmarkManuscripts?.total > 5 &&
                        <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                            <StyledButton type={"button"} color={"transparent"} onClick={() => {
                                history.push('/publisher/manuscripts');
                            }}>
                                {t('publishProposals.showAll')}
                            </StyledButton>
                        </Grid>
                        }
                    </CustomTabPanel>

                    <CustomTabPanel value={tabActive} index={2}>
                        <Grid item container direction={"row"} spacing={2}>
                            {manuscriptsData.toPublishManuscripts?.items?.map((application: PublisherSimilarBookListModel, index) => (
                                <NewPublisherBookShort key={index} manuscript={application}/>
                            ))}
                        </Grid>

                        {manuscriptsData.toPublishManuscripts?.items?.length === 0 && manuscriptsLoaded &&
                            <EmptyBookShort
                                title={t('publishProposals.notFound')}
                                description={''}
                                showButton={false}
                            />
                        }

                        {manuscriptsData.toPublishManuscripts?.total > 5 &&
                        <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                            <StyledButton type={"button"} color={"transparent"} onClick={() => {
                                history.push('/publisher/manuscripts');
                            }}>
                                {t('publishProposals.showAll')}
                            </StyledButton>
                        </Grid>
                        }
                    </CustomTabPanel>

                    <CustomTabPanel value={tabActive} index={3}>
                        <Grid item container direction={"row"} spacing={2}>
                            {manuscriptsData.notPublishManuscripts?.items?.map((application: PublisherSimilarBookListModel, index) => (
                                <NewPublisherBookShort key={index} manuscript={application}/>
                            ))}
                        </Grid>

                        {manuscriptsData.notPublishManuscripts?.items?.length === 0 && manuscriptsLoaded &&
                            <EmptyBookShort
                                title={t('publishProposals.notFound')}
                                description={''}
                                showButton={false}
                            />
                        }

                        {manuscriptsData.notPublishManuscripts?.total > 5 &&
                        <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                            <StyledButton type={"button"} color={"transparent"} onClick={() => {
                                history.push('/publisher/manuscripts');
                            }}>
                                {t('publishProposals.showAll')}
                            </StyledButton>
                        </Grid>
                        }
                    </CustomTabPanel>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default PublisherDashboard;

interface ManuscriptsData {
    newestManuscripts: ListData,
    toPublishManuscripts: ListData,
    bookmarkManuscripts: ListData,
    notPublishManuscripts: ListData
}
